// Globals
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, InputNumber } from 'antd';

// Components
import { Modal, ModalSize } from '@/components';
import { Button } from '@/ui';

// Models
import { IFiscalReceipt, IIssueOrderReturnItem } from '@/models/orders.model';

interface FiscalReturnModalProps {
  isOpen: boolean;
  isLoading: boolean;
  receipts?: IFiscalReceipt[];
  onClose: () => void;
  onSubmit: (payload: { all?: boolean; items?: IIssueOrderReturnItem[] }) => void;
}

// receiptId -> { lineIndex -> returned quantity }. A present index means the line is selected.
type Selection = Record<string, Record<number, number>>;

const FiscalReturnModal = ({ isOpen, isLoading, receipts, onClose, onSubmit }: FiscalReturnModalProps) => {
  const { t } = useTranslation(['orders']);

  // Only completed sale receipts can be refunded; returns reference their lines.
  const saleReceipts = useMemo(
    () => (receipts ?? []).filter((r) => r.type === 'sell' && r.status === 'done' && r.items?.length > 0),
    [receipts],
  );

  const [selected, setSelected] = useState<Selection>({});

  useEffect(() => {
    if (isOpen) {
      setSelected({});
    }
  }, [isOpen]);

  const isSelected = (receiptId: string, index: number) => selected[receiptId]?.[index] !== undefined;

  const toggleItem = (receipt: IFiscalReceipt, index: number) => {
    setSelected((prev) => {
      const lines = { ...(prev[receipt.id] ?? {}) };
      if (lines[index] !== undefined) {
        delete lines[index];
      } else {
        lines[index] = receipt.items[index].quantity; // default: full line quantity
      }
      return { ...prev, [receipt.id]: lines };
    });
  };

  const setQuantity = (receiptId: string, index: number, quantity: number) => {
    setSelected((prev) => ({ ...prev, [receiptId]: { ...(prev[receiptId] ?? {}), [index]: quantity } }));
  };

  const toggleReceipt = (receipt: IFiscalReceipt) => {
    setSelected((prev) => {
      const allSelected = Object.keys(prev[receipt.id] ?? {}).length === receipt.items.length;
      if (allSelected) {
        return { ...prev, [receipt.id]: {} };
      }
      const lines: Record<number, number> = {};
      receipt.items.forEach((item, i) => {
        lines[i] = item.quantity;
      });
      return { ...prev, [receipt.id]: lines };
    });
  };

  const totalSelected = Object.values(selected).reduce((sum, lines) => sum + Object.keys(lines).length, 0);

  // Whole order at full quantities → send `all`.
  const allSelectedFull =
    saleReceipts.length > 0 &&
    saleReceipts.every((r) => r.items.every((item, i) => selected[r.id]?.[i] === item.quantity));

  const toggleAll = () => {
    if (allSelectedFull) {
      setSelected({});
      return;
    }
    setSelected(
      Object.fromEntries(
        saleReceipts.map((r) => [r.id, Object.fromEntries(r.items.map((item, i) => [i, item.quantity]))]),
      ),
    );
  };

  const handleSubmit = () => {
    if (totalSelected === 0) {
      return;
    }

    if (allSelectedFull) {
      onSubmit({ all: true });
      return;
    }

    const items: IIssueOrderReturnItem[] = saleReceipts
      .filter((r) => Object.keys(selected[r.id] ?? {}).length > 0)
      .map((r) => ({
        receipt_id: r.id,
        lines: Object.entries(selected[r.id]).map(([index, quantity]) => ({ index: Number(index), quantity })),
      }));

    onSubmit({ items });
  };

  return (
    <Modal visible={isOpen} onCancel={onClose} size={ModalSize.LARGE}>
      <h3>{t('fiscal_return_title')}</h3>

      {saleReceipts.length === 0 ? (
        <p>{t('fiscal_return_no_sales')}</p>
      ) : (
        <>
          <Checkbox checked={allSelectedFull} onChange={toggleAll} style={{ marginBottom: 12 }}>
            {t('fiscal_return_select_all')}
          </Checkbox>

          {saleReceipts.map((receipt) => {
            const selectedCount = Object.keys(selected[receipt.id] ?? {}).length;

            return (
              <div key={receipt.id} style={{ marginBottom: 16, borderTop: '1px solid #eee', paddingTop: 8 }}>
                <Checkbox
                  checked={selectedCount === receipt.items.length}
                  indeterminate={selectedCount > 0 && selectedCount < receipt.items.length}
                  onChange={() => toggleReceipt(receipt)}
                >
                  <strong>{receipt.legal_type ? t(`orders_${receipt.legal_type}`) : '—'}</strong>
                  {receipt.fiscal_code ? ` · ${receipt.fiscal_code}` : ''}
                </Checkbox>

                <div style={{ paddingLeft: 24, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {receipt.items.map((item, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Checkbox checked={isSelected(receipt.id, index)} onChange={() => toggleItem(receipt, index)}>
                        {item.name} — {item.quantity} × {item.price} ₴
                      </Checkbox>
                      {isSelected(receipt.id, index) && (
                        <InputNumber
                          size="small"
                          min={0.001}
                          max={item.quantity}
                          step={1}
                          value={selected[receipt.id][index]}
                          onChange={(value) => setQuantity(receipt.id, index, Number(value) || 0)}
                          style={{ width: 90 }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          <Button type="primary" loading={isLoading} disabled={totalSelected === 0} onClick={handleSubmit}>
            {t('fiscal_return_submit')}
          </Button>
        </>
      )}
    </Modal>
  );
};

export default FiscalReturnModal;
